import React from "react";
import { useTranslation } from "react-i18next";
import { formatDuration } from "../../utils";
import { nextLocalDay } from "./recordingTimeline";
import type { ActivityBin } from "./activityModel";

export default function ActivityHistogram({
  height = 176,
  bins,
  compact = false,
  hourly = false,
  max,
  color,
  onSelect,
  selected,
}: {
  height?: number;
  bins: ActivityBin[];
  compact?: boolean;
  hourly?: boolean;
  max?: number;
  // Overrides the default bar colour, e.g. a person's identity colour.
  color?: string;
  onSelect?: (time: number) => void;
  selected?: number;
}) {
  const { t, i18n } = useTranslation();
  const peak = max ?? Math.max(0, ...bins.map(bin => bin.seconds));
  const dateLabel = (time: number) =>
    new Date(time).toLocaleString(
      i18n.resolvedLanguage,
      hourly
        ? { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }
        : { month: "short", day: "numeric" },
    );
  const label = (bin: ActivityBin) =>
    hourly
      ? `${new Date(bin.start).toLocaleString(i18n.resolvedLanguage, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZoneName: "short",
        })} – ${new Date(bin.end).toLocaleTimeString(i18n.resolvedLanguage, {
          hour: "2-digit",
          minute: "2-digit",
          timeZoneName: "short",
        })}`
      : bin.end > nextLocalDay(bin.start)
        ? `${dateLabel(bin.start)} – ${dateLabel(bin.end - 1)}`
        : dateLabel(bin.start);
  return (
    <div className={compact ? "min-w-0" : "grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-x-3"}>
      {!compact && (
        <div
          style={{ height }}
          className="flex flex-col justify-between text-right text-xs tabular-nums text-text-dim"
        >
          <span>{peak > 0 ? formatDuration(peak) : ""}</span>
          <span>{peak > 0 ? formatDuration(peak / 2) : ""}</span>
          <span>{formatDuration(0)}</span>
        </div>
      )}
      <div
        className="relative flex items-end border-b border-outline"
        style={{
          height: compact ? 40 : height,
          gap: compact ? 2 : 5,
          ...(!compact
            ? {
                backgroundImage:
                  "linear-gradient(to top, var(--color-outline, #e5e7eb) 1px, transparent 1px)",
                backgroundSize: "100% 50%",
              }
            : {}),
        }}
        role="group"
        aria-label={t("activity.daily")}
      >
        {bins.map(bin => {
          const title = `${label(bin)}: ${formatDuration(bin.seconds)}`;
          const bar = (
            <span
              className={`block w-full rounded-t-[3px] ${bin.seconds ? (color ? "" : "bg-info/70") : "bg-outline/60"} ${selected !== undefined && selected >= bin.start && selected < bin.end ? "!bg-info-strong" : ""}`}
              style={{
                // Compact previews keep only the baseline for empty bins.
                height: bin.seconds
                  ? `${Math.max(4, (bin.seconds / Math.max(1, peak)) * 100)}%`
                  : compact
                    ? 0
                    : 2,
                ...(bin.seconds && color ? { backgroundColor: color } : {}),
              }}
            />
          );
          return onSelect ? (
            <button
              key={bin.start}
              type="button"
              title={title}
              aria-label={title}
              aria-pressed={selected !== undefined && selected >= bin.start && selected < bin.end}
              onClick={() => onSelect(bin.start)}
              className="flex h-full min-w-0 flex-1 items-end rounded-t-sm hover:bg-info/10 focus-visible:outline-info"
            >
              {bar}
            </button>
          ) : (
            <div key={bin.start} title={title} className="flex h-full min-w-0 flex-1 items-end">
              {bar}
            </div>
          );
        })}
      </div>
      {!compact && bins.length > 0 && (
        <div className="col-start-2 mt-2 grid grid-cols-3 gap-2 text-xs text-text-dim">
          <span>{dateLabel(bins[0].start)}</span>
          <span className="text-center">
            {bins.length > 2 ? dateLabel(bins[Math.floor(bins.length / 2)].start) : ""}
          </span>
          <span className="text-right">
            {dateLabel(hourly ? bins[bins.length - 1].end : bins[bins.length - 1].end - 1)}
          </span>
        </div>
      )}
    </div>
  );
}
